/* eslint-disable max-params, complexity */
// TODO: file to be split in different files per content type, done for post, idea, wedo, question, wiki, survey

import { cacheDecorator } from '@ds/swymkit/tools'

import API from '~/services/api'
import { swymFetch } from '~/services/fetch-swym'

import { communityParser } from '~/entities/container/community/api/parser-community'
import type { ICommunity } from '~/entities/container/community/interfaces/community'
import type { IRendezvous } from '~/entities/container/conference/interfaces/rendezvous'
import { IDEA_CRUD_API } from '~/entities/content/idea/api/crud-ideas'
import { MEDIA_CRUD_API } from '~/entities/content/idea/api/crud-medias'
import { mediaParser } from '~/entities/content/media/api/parser-media'
import type { IMedia, IMediaResponseV2 } from '~/entities/content/media/interfaces/media'
import { POST_CRUD_API } from '~/entities/content/post/api/crud-posts'
import { QUESTION_CRUD_API } from '~/entities/content/question/api/crud-questions'
import { SURVEY_CRUD_API } from '~/entities/content/survey/api/crud-surveys'
import { WEDO_CRUD_API } from '~/entities/content/wedo/api/crud-wedos'
import { WIKI_CRUD_API } from '~/entities/content/wiki/api/crud-wikis'
import type { IWiki, WikiVisibility } from '~/entities/content/wiki/interfaces/wiki'
import type { IUser, UserHashKey } from '~/entities/user/user/interfaces/user'

import type { IConferenceParams, IParams } from '~/features-shared/content/api/interfaces/params'
import {
  contentParser,
  contentsParser,
  getContentModelFromSubjectUri,
  timelineParser,
} from '~/features-shared/content/api/parser-contents'
import type { IContent } from '~/features-shared/content/interfaces/content'
import { IContentModel } from '~/features-shared/content/interfaces/content-model'
import type { ISwymContent } from '~/features-shared/content/interfaces/swym-content'
import { getNthPartOfSubjectURI } from '~/features-shared/content/utils/subject_uri'
import type { IEntity, SUBJECT_6W_URI } from '~/features-shared/libs/interfaces/entity'
import type { ITimeline } from '~/features-shared/timeline/interfaces/timeline'

import type { ServiceTrackerEventOptions } from '~/features-generic/tracker/interfaces/tracker'

import type { ContentSwymServices, SwymService } from '~/interfaces/crud-service'
import type { ExaleadStatsResponse, SwymResponse } from '~/interfaces/services-dto'

const SURVEY_CREATE_EDIT_TIMEOUT = 60000 // 60s, increase timeout for this call, some calls exceed 30 seconds ! //IR-1081222

// #region feed
export function getCommunityTimeline(
  thread_id: string,
  from = '',
  limit = 20,
  limit_forward = 0,
  limit_backward = 0,
): Promise<SwymResponse<ITimeline[]>> {
  return swymFetch
    .get('/api/community/timeline', {
      // We don't want to filter with_synthesis and forceDb
      searchParams: [
        ['id', thread_id],
        ['with_synthesis', 'false'],
        ['forceDb', 'false'],
        ['limit', limit],
        ['from', from],
        ['limit_forward', limit_forward],
        ['limit_backward', limit_backward],
      ].filter(([, val]) => val),
    })
    .json<SwymResponse<ITimeline[]>>()
    .then(timelineParser)
}

export function getCommunityTimelineWithFilters(thread_id: string, from = '', limit = 20, opts: any = {}): Promise<SwymResponse<ITimeline[]>> {
  return swymFetch
    .post('/api/community/timeline', {
      json: {
        params: {
          id: thread_id,
          legacyFormat: false,
          with_synthesis: !from,
          query: opts.query || '#all',
          forceDb: false,
          limit,
          ...(from ? { from } : undefined),
          ...(from ? undefined : { start: 0 }),
          ...(opts.refine?.length ? { refine: opts.refine } : undefined),
        },
      },
    })
    .json<SwymResponse<ITimeline[]>>()
    .then(timelineParser)
}

export function getCommunityTimelineTags(thread_id: string): Promise<SwymResponse<ITimeline[]>> {
  return swymFetch
    .get('/api/community/timeline', {
      searchParams: [
        ['id', thread_id],
        ['with_synthesis', true],
        ['forceDb', false],
        ['limit', 0],
        ['nresults', 1],
        ['start', 0],
        ['query', '#all'],
        ['legacyFormat', false],
      ],
    })
    .json<SwymResponse<ITimeline[]>>()
}

export function postMessage(community_id: string, message: string, message_uri: string, user: IUser, parentCommentUri?: string) {
  return swymFetch.post(
    `/api/community/${community_id}/instantmessages`,
    {
      json: {
        author: {
          login: user.login,
          displayName: `${user.first_name} ${user.last_name}`,
        },
        accessState: null,
        commentUri: null,
        comments: null,
        endorsements: null,
        moderationState: null,
        parentCommentUri: parentCommentUri || null,
        richMessage: message,
      },
      headers: {
        'X-Request-Id': message_uri,
      },
    },
  )
    .json()
}
// #endregion feed

// #region get content
export function getConference(subject_uri: string) {
  return cacheDecorator(
    `conference-${subject_uri}`,
    () => swymFetch
      .get(`/api/conferences/${subject_uri}`)
      .json<SwymResponse<ISwymContent>>()
      .then(response => contentParser(response) as SwymResponse<IRendezvous>),
    2000,
  )
}

export function getMetadata(id: string) {
  return cacheDecorator(
    `get-media-metadata-${id}`,
    () => {
      return swymFetch
        .post('/api/media/getmetadata', {
          json: {
            params: {
              id_media: id,
            },
          },
        })
        .json<SwymResponse<ISwymContent>>()
        .then(contentParser)
    },
    60000,
  )
}

/**
 * Get media V2 API
 * Including response of derived type media
 */
export async function getMedia(subject_uri: string) {
  const isV2 = true
  const mediaId = subject_uri.split(':')
    .pop()

  return cacheDecorator(
    `media-${subject_uri}`,
    () => swymFetch
      .get(`/api/v2/media/${mediaId}`)
      .json<SwymResponse<IMediaResponseV2>>()
      .then(async (response) => {
        const mediaParsed = await mediaParser(response, isV2)
        mediaParsed.result.model = IContentModel.Media
        return mediaParsed
      }),
    2000,
  )
}

export async function fetchSwymContent<T extends IContent>(
  subject_uri: string,
  metadataOnly?: boolean,
  contentType = '',

  /**
   * Tracker event options.
   * @default undefined
   */
  tracker?: ServiceTrackerEventOptions,
): Promise<T | undefined> {
  const type = contentType || getContentModelFromSubjectUri(subject_uri)

  let response: SwymResponse<IContent>
  if (type === IContentModel.Conference) {
    response = await getConference(subject_uri)
  }
  else if (type === IContentModel.Idea || type === IContentModel.DerivedIdea) {
    response = await IDEA_CRUD_API.read({ subject_uri })
  }
  else if (type === IContentModel.Media) {
    response = (metadataOnly)
      ? await getMetadata(subject_uri)
      : await MEDIA_CRUD_API.read({ subject_uri })
  }
  else if (type === IContentModel.Question) {
    response = await QUESTION_CRUD_API.read({ subject_uri })
  }
  else if (type === IContentModel.Survey) {
    response = await SURVEY_CRUD_API.read({ subject_uri })
  }
  else if (type === IContentModel.Wedo) {
    const uri = subject_uri.split('dsxplan:')
      .pop()!
    response = await WEDO_CRUD_API.read({ subject_uri: uri })
  }
  else if (type === IContentModel.Wiki) {
    response = await WIKI_CRUD_API.read({ subject_uri }, tracker)
  }
  else {
    response = await POST_CRUD_API.read({ subject_uri })
  }

  if (response.result)
    return response.result as T
}
// #endregion

// #region Services generator
interface ServiceOptions {
  contentType?: IContentModel
  api: string | ((communityId?: string) => string)
  openApi?: boolean
}

function getApiUri(api: ServiceOptions['api'], communityId?: string) {
  return typeof api === 'function' ? api(communityId) : api
}

function getService<T extends IEntity>({ contentType, api, openApi }: ServiceOptions): SwymService<T> {
  return {
    async count(params) {
      const response = await swymFetch
        .post(getApiUri(api, params.community_id), {
          json: {
            params: {
              ...(contentType ? { content_type: [contentType] } : {}),
              ...params,
            } as IParams,
          },
        })
        .json<ExaleadStatsResponse>()

      return {
        total: response.nmatch,
        data: [],
      }
    },
    async getAll(params) {
      if (openApi) {
        const isV2 = true
        const searchParams = {
          ...(params.derivedTypeId ? { derived_type_id: params.derivedTypeId } : {}),
          ...(contentType === IContentModel.Media
            ? { page_number: (params.page - 1).toString(), page_size: params.limit.toString() } // Index starts from 0 in Media V2 API
            : { page: params.page.toString(), limit: params.limit.toString() }),
        }

        const response = await swymFetch
          .get(getApiUri(api, params.community_id), { searchParams })
          .json<SwymResponse<ISwymContent[]>>()

        return {
          total: Number.parseInt(response.nb_result || '0') || 0,
          data: (contentsParser(response, isV2).result) as unknown as T[],
        }
      }

      const response = await swymFetch
        .post(getApiUri(api, params.community_id), {
          json: {
            params: {
              ...(contentType ? { content_type: contentType } : {}),
              ...params,
            } as IParams,
          },
        })
        .json<SwymResponse<ISwymContent[]> >()
      return {
        total: Number.parseInt(response.nb_result || '0') || 0,
        data: contentsParser(response).result as unknown as T[],
      }
    },
  }
}

function getServices<T extends IContent>(): ContentSwymServices<T> {
  const contentTypes = [
    IContentModel.Post,
    IContentModel.DerivedPost,
    IContentModel.Media,
    IContentModel.Idea,
    IContentModel.DerivedIdea,
    IContentModel.Question,
    IContentModel.Wiki,
    IContentModel.Survey,
    IContentModel.Conference,
    IContentModel.Wedo,
  ]

  const apis = {
    [IContentModel.Post]: API.Post.Swym.Content,
    [IContentModel.Conference]: API.Conference.Swym.Content,
    [IContentModel.Survey]: API.Survey.Swym.Content,
    [IContentModel.Question]: API.Question.Swym.Content,
    [IContentModel.Wiki]: API.Wiki.Swym.Content,
    [IContentModel.Idea]: API.Idea.Swym.Content,
    [IContentModel.Wedo]: API.Wedo.Swym.Content,

    [IContentModel.Media]: API.Media.Swym.ContentV2,
    [IContentModel.DerivedIdea]: API.Idea.Swym.ContentV2,
    [IContentModel.DerivedPost]: API.Post.Swym.ContentV2,
  } as Partial<Record<IContentModel, string | typeof API.Idea.Swym.ContentV2>>

  const apisOpenApi = [IContentModel.DerivedPost, IContentModel.DerivedIdea, IContentModel.Media]

  const services = contentTypes.reduce((acc, contentType) => {
    acc[contentType] = getService<T>({
      contentType,
      api: apis[contentType] as string,
      openApi: apisOpenApi.includes(contentType),
    })
    return acc
  }, {} as ContentSwymServices<T>)

  return services
}

export default getServices<IContent | IMedia>()

export const getContentService = getService<IContent>({ api: API.Post.Swym.Content })
// #endregion

// #region Update apis

// TODO: doc
export function updateConference(params: IConferenceParams, tracker?: ServiceTrackerEventOptions) {
  return swymFetch
    .put(`/api/conferences/${params.subject6wuri}`, {
      json: params,
      tracker,
    })
    .json<SwymResponse<ISwymContent>>()
    .then(contentParser)
}
// #endregion

// #region create content
export interface ICreationParams {
  title?: string
  community_subject_uri?: string
  message?: string
  tags6w?: any[]
  taggingActions?: any
  published?: boolean
}

export interface IWikiCreationParams {
  /** Parent wiki page subject URI (should be at least the root page). */
  parent_id: SUBJECT_6W_URI

  /**
   * Position of the new page in the parent's children list.
   * @info Valid position: [1 to nbChildren]
   * @info If 0 or undefined, will add the new page at the end of the parent' children list.
   * @default undefined
   */
  position?: number

  /**
   * Should lock/unlock the wiki page edition.
   * @info 0: edition enabled.
   * @info 1: edition disabled.
   */
  locked?: IWiki['locked']

  /**
   * Should lock/unlock the wiki page and all its children (deep).
   * @info "locked" attribute will be ignore if this one is provided.
   */
  lockedAll?: boolean
  visibility?: WikiVisibility
  usersToAdd?: UserHashKey[]
  usersToRemove?: UserHashKey[]
}

interface ISurveyCreationParams {
  title?: string
  community_subject_uri?: string
  questions?: any[]
  startHour?: string
  endHour?: string
  endDate?: string
  startDate?: string
  showResultsAfterVote?: boolean
  tags6w?: any[]
  taggingActions?: any
  published?: boolean
}

export function createSurvey({ title = '', community_subject_uri = '', questions = [], startHour = '', endHour = '', endDate = '', startDate = '', showResultsAfterVote = false, published, tags6w, taggingActions }: ISurveyCreationParams, tracker?: ServiceTrackerEventOptions) {
  return swymFetch.post('/api/poll/createpoll', {
    json: {
      params: {
        title,
        community_id: community_subject_uri,
        showResultsAfterVote,
        startDate,
        startHour,
        endDate,
        endHour,
        questions,
        published: published ? 1 : 0,
        ...(taggingActions ? { taggingActions6w: taggingActions } : undefined),
        ...(tags6w ? { tags6w } : undefined),
      },
    },
    tracker,
    timeout: SURVEY_CREATE_EDIT_TIMEOUT,
  })
    .json<SwymResponse<ISwymContent>>()
    .then(contentParser)
}

export function createConference(data: any, tracker?: ServiceTrackerEventOptions) {
  return swymFetch.post(`/api/communities/${data.community_subject_uri}/conferences`, { json: data, tracker })
    .json<SwymResponse<ISwymContent>>()
    .then(contentParser)
}

export function createVBrickConference(data: any, VBrickID: string, tracker?: ServiceTrackerEventOptions) {
  // vbrick url https://xxx.xxx.xxx/#/events/5e29e242-0947-4e0e-8c45-ecef25794a75
  const parsedID = VBrickID.split('/events/')[1] // we need the id for the creation
  return swymFetch.post(`/api/communities/${data.community_subject_uri}/conferences/providers/vbrick/${parsedID}`, { json: data, tracker })
    .json<SwymResponse<ISwymContent>>()
    .then(contentParser)
}

export function createRitual(data: any, community_subject_uri: string, tracker?: ServiceTrackerEventOptions) {
  return swymFetch.post(`/api/communities/${community_subject_uri}/rituals`, { json: data, tracker })
    .json<SwymResponse<ISwymContent>>()
    .then(contentParser)
}
// #endregion

// #region delete content
export function deleteConference(content_subject_uri: string, tracker?: ServiceTrackerEventOptions) {
  return swymFetch.delete(`/api/conferences/${content_subject_uri}`, { tracker })
}

export function deleteRitual(content_subject_uri: string, tracker?: ServiceTrackerEventOptions) {
  return swymFetch.delete(`/api/rituals/${content_subject_uri}`, { tracker })
}
// #endregion delete content

export function forwardMessage(data: any) {
  return swymFetch
    .post(`/api/community/${encodeURIComponent(data.threadUri)}/instantmessages?source_uri=${encodeURIComponent(data.content.subject_uri)}`)
}

export function forwardInstantMessageFromExternalDm(data: any) {
  return swymFetch
    .post(`/api/community/${encodeURIComponent(data.threadUri)}/instantmessages?source_uri=${encodeURIComponent(data.content.subject_uri)}&source_conv=${encodeURIComponent(data.sourceConv)}`)
}

export function exportAsPdf(content_subject_uri: string, content_type: IContentModel, show_watermark: boolean) {
  const map: Partial<Record<IContentModel, string>> = {
    [IContentModel.Post]: 'posts',
    [IContentModel.Idea]: 'ideas',
    [IContentModel.Question]: 'questions',
    [IContentModel.Wiki]: 'wiki_pages',
    [IContentModel.WikiTree]: 'wikipages',
  }
  const type = map[content_type]
  const id = getNthPartOfSubjectURI(content_subject_uri, 4)
  return swymFetch.post(`/api/v2/${type}/${id}/pdf`, {
    json: {
      show_watermark,
      // show_page_number: true,
      // generate_toc: true,
    },
  })
}

export function exportWikiTreeAsPdf(URIs: string[], show_watermark: boolean) {
  return swymFetch.post('/api/v2/wikipages/pdf', {
    json: {
      content_uris: URIs,
      // title: 'Title',
      show_watermark,
    // show_page_number: true,
    // generate_toc: true,
    },
  })
}
/**
 * @returns personal space data
 */
export function getPersonalSpace() {
  return cacheDecorator(
    'personal-space',
    () => swymFetch
      .get(`/api/personalspace`)
      .json<SwymResponse<ICommunity>>()
      .then(communityParser),
    5000,
  )
}

/**
 * Creates personal space
 * @returns created space data
 */
export function createPersonalSpace() {
  return swymFetch
    .post('/api/personalspace', {
      body: '',
    })
    .json<SwymResponse<ICommunity>>()
    .then(communityParser)
}

/**
 * Get user activity
 * @param options - Options.
 * @param options.user_id - user login id
 * @param options.limit - limit of data
 * @param options.containerType - Filter by content type (e.g. Post/Media/Idea..)
 * @param options.activityType - Filter of All/Content/Comment
 * @param options.from - from as index here to load next page
 * @returns list of activity
 */
export function getUserActivity(
  options: {
    user_id: string
    limit: number
    from?: any
    containerType?: string
    activityType?: number
  },
): Promise<SwymResponse<ITimeline[]>> {
  const params = [
    ['id', options.user_id],
    ['with_synthesis', false],
    ['forceDb', false],
    ['limit', options.limit],
    ['from', options.from],
  ]

  if (options.containerType)
    params.push(['containerType', options.containerType])
  if (options.activityType || options.activityType === 0)
    params.push(['activityType', options.activityType])

  return swymFetch
    .get('/api/user/activity', {
      searchParams: params.filter(([, val]) => val !== undefined && val !== ''),
    })
    .json<SwymResponse<ITimeline[]>>()
    .then(timelineParser)
}

/**
 * Get user activity by searchquery
 * @param user_id - user login id
 * @param limit - limit of data
 * @param query - Filter by query of search
 * @returns list of activity
 */
export function getUserActivityByQuery(
  options: {
    user_id: string
    limit: number
    query: string | undefined
    from?: string | undefined
  },
): Promise<SwymResponse<ITimeline[]>> {
  return swymFetch.post('/api/user/activity', {
    json: {
      params: {
        id: options.user_id,
        forceDb: false,
        legacyFormat: false,
        limit: options.limit,
        query: options.query,
        from: options.from,
        with_synthesis: true,
      },
    },
  })
    .json<SwymResponse<ITimeline[]>>()
    .then(timelineParser)
}
